// Core
import { useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { motion, useAnimation } from 'framer-motion';
import { Box, Image, Divider, Button, Grid, Text, Flex, useColorModeValue } from '@chakra-ui/react';
import { NavLink, Link } from 'react-router-dom';

// Assets
import { apiService } from 'services/ApiService';
import placeholderImage from 'assets/img/empty-plan.png';
import { ReactComponent as Lines } from 'assets/svg/lines.svg';
import crystal2 from 'assets/img/details/crystal-2.png';

// Components
import { StarsIcon, CheckIcon, CheckActiveIcon, EntryArrowIcon } from 'components/Icons/Icons';
import Card from 'components/Card/Card';

const PAY_ERROR_FALLBACK = 'Something went wrong while creating your payment. Please try again.';

const payErrorMessage = (response) => {
	const message = Array.isArray(response?.message) ? response.message[0] : response?.message;

	return typeof message === 'string' && message.length > 0 ? message : PAY_ERROR_FALLBACK;
};

const OnePayItem = ({ title, description, isPopular }) => {
    return (
    	<Flex gap="8px" mb="10px">
    		<CheckActiveIcon fontSize="20px" />
			<Text><span style={{ 
				whiteSpace: 'nowrap', 
				color: '#099BFF', 
				fontWeight: '600', 
				fontStyle: 'italic' }}>{title}</span> {description}</Text>
		</Flex>
	);
};

const OnePay = ({ giveaway, setIsOneTimePaymentEnabled }) => {
	const textColor = '#9FABCB';
	const bgDivider = 'linear-gradient(90deg, rgba(56, 169, 225, 0.00) 9.63%, #38A9E1 37.41%, #9EDFFF 50%, #38A9E1 60.02%, rgba(56, 169, 225, 0.00) 91.14%)';
	const [isFetching, setIsFetching] = useState(false);
	const [payError, setPayError] = useState(null);
	const [viewFullInclusions, setViewFullInclusions] = useState(false);

	const dividerColor = useColorModeValue('#C8D3FF', '#252d47');

    const { data, isLoading } = useQuery({
        queryKey: ['one-pay', giveaway],
        queryFn: async () => {
            const response = await apiService.getOnePay({id: giveaway.id});
            const formatResponse = response.data.filter(item => item.paymentType === 'one-time');

            if(formatResponse?.length === 0){
            	setIsOneTimePaymentEnabled(false);
            }

            return formatResponse;
        },
        refetchOnWindowFocus: false
    });

    const handlePay = async (dto) => {
    	setIsFetching(true);
    	setPayError(null);

    	try {
	    	const response = await apiService.getOnePayCreate(dto);

	    	if(response?.data?.url) {
	    		window.location.href = response.data.url;
	    		return;
	    	}

	    	setPayError({
	    		plan: dto.plan,
	    		message: payErrorMessage(response)
	    	});
    	} catch {
    		setPayError({
    			plan: dto.plan,
    			message: PAY_ERROR_FALLBACK
    		});
    	} finally {
	        setIsFetching(false);
    	}
    };

    return (
    	<>
	    	{ isLoading ? ( 
	            <Box textAlign="center" py="50px">Load ...</Box>
	        ) : (
	        	<>
		        <Grid templateColumns={{ base: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr', lg: '1fr 1fr 1fr 1fr' }} gap="24px" templateRows='1fr' mt={{base: '16px', sm: '44px'}} mb="40px">
				    { data?.map((item, index) => (
				    	<Box
				    		key={index}
				    		position="relative"
				    		overflow="hidden">
					        <Card zIndex="2" padding="0 0 0px 0" key={index} overflow="hidden" position="relative">
					        	<Box 
					        		bg="#099BFF" 
					        		borderRadius="full" 
					        		filter="blur(56px)" 
					        		w="100px" h="100px" right="0" bottom="10%" 
					        		position="absolute" zIndex="2" />

					        	<Box p="16px" zIndex="3">
					        		<Flex gap="8px" alignItems="center">
					        			<Box w={{base: '17px', md: '24px', lg: '30px'}} h={{base: '17px', md: '24px', lg: '30px'}}>
					        				<Image src={item?.image ?? placeholderImage} alt={item?.name} 
					        				borderRadius="4px" 
					        				w={{base: '17px', md: '24px', lg: '30px'}} 
					        				h={{base: '17px', md: '24px', lg: '30px'}} 
					        				maxW={{base: '17px', md: '24px', lg: '30px'}}
					        				objectFit="cover" />
					        			</Box>
					        			<Text variant="header" fontSize={{base: '16px', sm: '24px'}} textTransform="unset" fontStyle="normal">
					        				{item?.name} - ${item?.price / 100}
					        			</Text>
					        		</Flex>

					        		<Text mt="4px" zIndex="9" fontSize={{base: '13px', md: '16px'}} color={textColor}>{item.description}</Text>

					        		<Divider mt="8px" position="relative" zIndex="9" borderColor="unset" borderBottomColor={dividerColor} borderBottomWidth="1px" />

					        		<Text variant="header" fontSize={{base: '16px', sm: '24px'}} fontStyle="normal" mt="16px">Free Entries</Text>
					        		<Text variant="header" fontSize={{base: '40px', sm: '64px'}} fontStyle="normal" mb="16px">{item?.entries}</Text>
					        		
									<a href={item?.link} target="_blank" rel="noopener noreferrer">
								        <Button variant="button-lg-g" 
								        	isLoading={isFetching} 
								        	onClick={() => handlePay({
								        		giveaway: giveaway?.id,
								        		plan: item.id
								        	})}
								        >Select</Button>
								    </a>
								    { payError?.plan === item.id && (
								    	<Text mt="8px" fontSize="14px" color="#FF3B30" position="relative" zIndex="9">{payError.message}</Text>
								    )}
					        	</Box>

					        	<Lines className="abstract-lines" style={{ zIndex: 1, left: '-50%', top: '-30%', transform: 'scale(0.7)'}} />
					        	<Image src={item?.image ?? placeholderImage} 
					        		position="absolute" zIndex="1" w="35%" right="20px" top="30%" opacity="0.2" />
					        </Card>
				        </Box>
				    ))}	
		        </Grid>

		        <Flex gap="8px" alignItems="center">
		        	<Divider position="relative" zIndex="9" borderColor="unset" borderBottomColor={dividerColor} borderBottomWidth="1px" />
		        	<Flex gap="5px" pt="2px" cursor="pointer"
		        		onClick={
		        			() => setViewFullInclusions(!viewFullInclusions)
		        		}
		        		>
		        		<Text color="#099BFF" whiteSpace="nowrap" fontWeight="500">View Full Inclusions</Text>
		        		<EntryArrowIcon fontSize="24px" mt="-3px" 
		        			transform={ viewFullInclusions ? 'rotate(0deg)' : 'rotate(180deg)' }
		        		/>
		        	</Flex>
		        	<Divider position="relative" zIndex="9" borderColor="unset" borderBottomColor={dividerColor} borderBottomWidth="1px" />
		        </Flex>

		        <Grid
		        	overflow="hidden"
		        	h={ viewFullInclusions ? '100%' : '0' }
		        	templateColumns={{ sm: '1fr', md: '1fr 1fr', lg: '1fr 1fr 1fr' }} 
		        	gap="24px" 
		        	templateRows='1fr' 
		        	mt="44px"
		        	mb="86px">
		        	{ data?.map((item, index) => (
		        		<Box key={index} position="relative">
		        			<Flex gap="8px" alignItems="center" mb="10px">
			        			<Box w="20px" h="20px">
			        				<Image src={item?.image ?? placeholderImage} alt={item?.name} 
			        				borderRadius="4px" 
			        				w="20px" 
			        				h="20px" 
			        				maxW="20px"
			        				objectFit="cover" />
			        			</Box>
			        			<Text 
			        				variant="title" 
			        				fontSize="18px" 
			        				fontWeight="800 !important">
			        				{item?.name} - ${item?.price / 100}
			        			</Text>
			        		</Flex>
		        			<OnePayItem title={ item?.partnerDiscountsAccess } description="access to 1,500+ Partner discounts" />
		        			<OnePayItem title={
		        				item?.vipInvites === 'none' ? '' : item?.vipInvites === 'vip' ? 'VIP' : 'VIP+'
							} description={
								item?.vipInvites === 'none' ? 
									'Ivitation to all games' 
								: 'invitation to all events'
							} />
							<OnePayItem title={
								item?.entries > 1 ? `${item?.entries} entries` : `${item?.entries} entrie`
							} description={`to win ${giveaway?.title}`} />
								        		
				    	</Box>
		        	))}	
				</Grid>	

				</>	        
		    )}
	    </>
    );
};


export default OnePay;